import React, { useState, useEffect } from "react"

import { Button } from "../../../../components/shared/Buttons"
import {
  InstagramIcon,
  TikTokIcon,
  XIcon,
  FacebookIcon,
  YouTubeIcon,
  LinkedInIcon,
  SubstackIcon,
  PinterestIcon
} from "../../../../components/shared/icons/SocialIcons"
import { Input, Select, Toggle } from "../../../../components/shared/Inputs"
import Typography from "../../../../components/shared/Typography"
import { resolveBuilderPageSlug } from "../../utils/urlUtils"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"

const FooterForm = ({ theme, practice, onUpdate, onClose }) => {
  const { pages } = useWebsiteBuilderV2()
  const defaultServicesSlug = resolveBuilderPageSlug(practice, "services", "services")
  const [ctaType, setCtaType] = useState(theme?.footer?.ctaType || "page")
  const [buttonText, setButtonText] = useState(theme?.footer?.buttonText || "Book Now")
  const [page, setPage] = useState(theme?.footer?.page || defaultServicesSlug)
  const [service, setService] = useState(theme?.footer?.service || "")
  const [disclaimerText, setDisclaimerText] = useState(theme?.footer?.disclaimerText || "")
  const [ctaButtonBackground, setCtaButtonBackground] = useState(
    theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFFFFF"
  )
  const [ctaButtonText, setCtaButtonText] = useState(
    theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#55433f"
  )
  const [showHome, setShowHome] = useState(theme?.footer?.showHome || false)

  // Social links state
  const [socialLinks, setSocialLinks] = useState({
    instagram: theme?.footer?.socialLinks?.instagram || "",
    tiktok: theme?.footer?.socialLinks?.tiktok || "",
    x: theme?.footer?.socialLinks?.x || "",
    facebook: theme?.footer?.socialLinks?.facebook || "",
    youtube: theme?.footer?.socialLinks?.youtube || "",
    linkedin: theme?.footer?.socialLinks?.linkedin || "",
    substack: theme?.footer?.socialLinks?.substack || "",
    pinterest: theme?.footer?.socialLinks?.pinterest || ""
  })

  // Dynamic page options from context
  const pageOptions = pages
    ?.filter((page) => page.defaultPageType !== "home") // Exclude home page (by defaultPageType)
    ?.map((page) => ({
      value: page.slug,
      label: page.title
    })) || [
    { value: defaultServicesSlug, label: "Services" },
    { value: resolveBuilderPageSlug(practice, "about", "about"), label: "About" },
    { value: resolveBuilderPageSlug(practice, "contact", "contact"), label: "Contact" }
  ]

  // Service options from practice
  const serviceOptions =
    practice?.services?.map((service) => ({
      value: service.id.toString(),
      label: service.name
    })) || []

  // Sync form state with theme prop when it changes
  useEffect(() => {
    setCtaType(theme?.footer?.ctaType || "page")
    setButtonText(theme?.footer?.buttonText || "Book Now")
    setPage(theme?.footer?.page || defaultServicesSlug)
    setService(theme?.footer?.service || "")
    setDisclaimerText(theme?.footer?.disclaimerText || "")
    setCtaButtonBackground(theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFFFFF")
    setCtaButtonText(theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#55433f")
    setShowHome(theme?.footer?.showHome || false)
    setSocialLinks({
      instagram: theme?.footer?.socialLinks?.instagram || "",
      tiktok: theme?.footer?.socialLinks?.tiktok || "",
      x: theme?.footer?.socialLinks?.x || "",
      facebook: theme?.footer?.socialLinks?.facebook || "",
      youtube: theme?.footer?.socialLinks?.youtube || "",
      linkedin: theme?.footer?.socialLinks?.linkedin || "",
      substack: theme?.footer?.socialLinks?.substack || "",
      pinterest: theme?.footer?.socialLinks?.pinterest || ""
    })
  }, [theme])

  // Auto-select first option when ctaType changes
  useEffect(() => {
    if (ctaType === "page" && pageOptions.length > 0) {
      setPage(pageOptions[0].value)
    } else if (ctaType === "service" && serviceOptions.length > 0) {
      setService(serviceOptions[0].value)
    }
  }, [ctaType])

  // Apply temporary preview when values change (logo is set in Sitewide styles → Logo)
  useEffect(() => {
    const footerData = {
      footer: {
        ...theme?.footer,
        ctaType,
        buttonText,
        page,
        service,
        disclaimerText,
        ctaButtonBackground,
        ctaButtonText,
        showHome,
        socialLinks,
        // Map form data to the correct settings structure
        ctaAction: ctaType,
        ctaTarget: ctaType === "service" ? service : page
      }
    }
    onUpdate(footerData, { temporary: true })
  }, [ctaType, buttonText, page, service, disclaimerText, ctaButtonBackground, ctaButtonText, showHome, socialLinks])

  // Handle social link changes
  const handleSocialLinkChange = (platform, value) => {
    setSocialLinks((prev) => ({
      ...prev,
      [platform]: value
    }))
  }

  const handleSave = () => {
    const saveData = {
      footer: {
        ...theme?.footer,
        ctaType,
        buttonText,
        page,
        service,
        disclaimerText,
        ctaButtonBackground,
        ctaButtonText,
        showHome,
        socialLinks,
        // Map form data to the correct settings structure
        ctaAction: ctaType,
        ctaTarget: ctaType === "service" ? service : page
      }
    }
    onUpdate(saveData)
    onClose()
  }

  const handleCancelClick = () => {
    // Revert to stored theme when closing without saving
    const storedCtaType = theme?.footer?.ctaType || "page"
    const storedPage = theme?.footer?.page || defaultServicesSlug
    const storedService = theme?.footer?.service || ""
    const storedSocialLinks = theme?.footer?.socialLinks || {
      instagram: "",
      tiktok: "",
      x: "",
      facebook: "",
      youtube: "",
      linkedin: "",
      substack: "",
      pinterest: ""
    }

    const storedFooterData = {
      footer: {
        ...theme?.footer,
        ctaType: storedCtaType,
        buttonText: theme?.footer?.buttonText || "Book Now",
        page: storedPage,
        service: storedService,
        disclaimerText: theme?.footer?.disclaimerText || "",
        ctaButtonBackground: theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFFFFF",
        ctaButtonText: theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#55433f",
        showHome: theme?.footer?.showHome || false,
        socialLinks: storedSocialLinks,
        // Map form data to the correct settings structure
        ctaAction: storedCtaType,
        ctaTarget: storedCtaType === "service" ? storedService : storedPage
      }
    }
    onUpdate(storedFooterData, { temporary: true })
    onClose()
  }

  return (
    <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
      {/* Logo is set in Sitewide styles → Logo */}
      <div className="px-8 pt-6">
        <Typography variant="small" className="text-gray">
          The logo shown in the footer is set in <strong>Sitewide styles → Logo</strong>. Edit it there to change the
          image or text logo for both header and footer.
        </Typography>
      </div>

      {/* Navigation Settings */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Navigation
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Configure which navigation links to show in the footer.
        </Typography>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <Typography variant="body" className="font-medium text-gray-dark">
              Show [Home] link
            </Typography>
            <Typography variant="small" className="text-gray">
              Include a [Home] link in the navigation menu
            </Typography>
          </div>
          <div className="ml-4">
            <Toggle checked={showHome} onChange={setShowHome} />
          </div>
        </div>
      </div>

      {/* CTA Button Section */}
      <div className="px-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Call-to-action button
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Guide visitors to the next step with a clear call-to-action button.
        </Typography>
        <div className="mb-4 flex items-center space-x-6">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="page"
              checked={ctaType === "page"}
              onChange={() => setCtaType("page")}
              className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Page</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="service"
              checked={ctaType === "service"}
              onChange={() => setCtaType("service")}
              className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Service</span>
          </label>
        </div>
        <Input
          label="Button text"
          value={buttonText}
          onChange={(e) => setButtonText(e.target.value)}
          className="mb-4"
        />
        {ctaType === "page" ? (
          <Select
            label="Page"
            value={page}
            onChange={(e) => setPage(e.target.value)}
            options={pageOptions}
            className="mb-6"
          />
        ) : (
          <Select
            label="Service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            options={serviceOptions}
            placeholder="Select a service"
            className="mb-6"
          />
        )}

        {/* Button Colors */}
        <div className="mb-8 space-y-4">
          <Typography variant="title" className="text-gray-dark">
            Button colors
          </Typography>

          {/* Background Color */}
          <div>
            <Typography variant="small" className="mb-2 font-medium text-gray-dark">
              Background color
            </Typography>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={ctaButtonBackground}
                  onChange={(e) => setCtaButtonBackground(e.target.value)}
                  className="h-10 w-20 cursor-pointer rounded-lg border-2 border-gray-300 transition-all hover:border-teal"
                  style={{ backgroundColor: ctaButtonBackground }}
                />
              </div>
              <input
                type="text"
                value={ctaButtonBackground}
                onChange={(e) => setCtaButtonBackground(e.target.value)}
                placeholder="#FFFFFF"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm uppercase focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal"
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <Typography variant="small" className="mb-2 font-medium text-gray-dark">
              Text color
            </Typography>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={ctaButtonText}
                  onChange={(e) => setCtaButtonText(e.target.value)}
                  className="h-10 w-20 cursor-pointer rounded-lg border-2 border-gray-300 transition-all hover:border-teal"
                  style={{ backgroundColor: ctaButtonText }}
                />
              </div>
              <input
                type="text"
                value={ctaButtonText}
                onChange={(e) => setCtaButtonText(e.target.value)}
                placeholder="#55433f"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm uppercase focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Text Section */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Footer text (optional)
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Add any text that you want to include in the footer.
        </Typography>
        <textarea
          value={disclaimerText}
          onChange={(e) => setDisclaimerText(e.target.value)}
          placeholder="Enter footer text here..."
          className="h-32 w-full resize-none rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal"
        />
      </div>

      {/* Social Links Section */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Social Links
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Add your social media links. Icons will only appear if you provide a URL.
        </Typography>

        <div className="space-y-4">
          {/* Instagram */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-pink-100">
              <InstagramIcon className="size-5 text-pink-600" />
            </div>
            <Input
              label="Instagram"
              value={socialLinks.instagram}
              onChange={(e) => handleSocialLinkChange("instagram", e.target.value)}
              placeholder="https://instagram.com/yourusername"
              className="flex-1"
            />
          </div>

          {/* TikTok */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-black">
              <TikTokIcon className="size-5 text-white" />
            </div>
            <Input
              label="TikTok"
              value={socialLinks.tiktok}
              onChange={(e) => handleSocialLinkChange("tiktok", e.target.value)}
              placeholder="https://tiktok.com/@yourusername"
              className="flex-1"
            />
          </div>

          {/* X (Twitter) */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-black">
              <XIcon className="size-5 text-white" />
            </div>
            <Input
              label="X (Twitter)"
              value={socialLinks.x}
              onChange={(e) => handleSocialLinkChange("x", e.target.value)}
              placeholder="https://x.com/yourusername"
              className="flex-1"
            />
          </div>

          {/* Facebook */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-blue-600">
              <FacebookIcon className="size-5 text-white" />
            </div>
            <Input
              label="Facebook"
              value={socialLinks.facebook}
              onChange={(e) => handleSocialLinkChange("facebook", e.target.value)}
              placeholder="https://facebook.com/yourpage"
              className="flex-1"
            />
          </div>

          {/* YouTube */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-red-600">
              <YouTubeIcon className="size-5 text-white" />
            </div>
            <Input
              label="YouTube"
              value={socialLinks.youtube}
              onChange={(e) => handleSocialLinkChange("youtube", e.target.value)}
              placeholder="https://youtube.com/@yourchannel"
              className="flex-1"
            />
          </div>

          {/* LinkedIn */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-blue-700">
              <LinkedInIcon className="size-5 text-white" />
            </div>
            <Input
              label="LinkedIn"
              value={socialLinks.linkedin}
              onChange={(e) => handleSocialLinkChange("linkedin", e.target.value)}
              placeholder="https://linkedin.com/in/yourprofile"
              className="flex-1"
            />
          </div>

          {/* Substack */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-[#FF6719]">
              <SubstackIcon className="size-5 text-white" />
            </div>
            <Input
              label="Substack"
              value={socialLinks.substack}
              onChange={(e) => handleSocialLinkChange("substack", e.target.value)}
              placeholder="https://yourname.substack.com"
              className="flex-1"
            />
          </div>

          {/* Pinterest */}
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-[#E60023]">
              <PinterestIcon className="size-5 text-white" />
            </div>
            <Input
              label="Pinterest"
              value={socialLinks.pinterest}
              onChange={(e) => handleSocialLinkChange("pinterest", e.target.value)}
              placeholder="https://pinterest.com/yourname"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="sticky bottom-0 z-10 flex items-center justify-end space-x-3 border-t border-gray-light bg-white px-8 py-6">
        <Button type="secondary" onClick={handleCancelClick} className="px-6">
          Close
        </Button>
        <Button type="primary" onClick={handleSave} className="px-6">
          Save
        </Button>
      </div>
    </div>
  )
}

export default FooterForm
